/**
 * src/lib/contracts/renderContract.js — Per-view entity render contract.
 *
 * Every view runs its candidate entities through applyContract() which:
 *   1. Validates the entity type is declared for that view
 *   2. Checks required fields
 *   3. Applies the view-specific filter
 *   4. Logs each exclusion with reason → app_error_logs.render_exclusion
 *
 * Extending: add an entry to VIEW_CONTRACTS and update docs/RENDER_CONTRACT.md.
 */

import { logEvent, getTraceContext } from '../observability';

const PHASE_TAG = 'phase_b2';

const notArchived = (e) => !e.archived_at;
const alwaysAllow = () => true;

export const VIEW_CONTRACTS = {
  gantt: {
    entities: {
      task: {
        requiredFields: ['task_name'],
        filter: notArchived,
        excludeReasons: ['archived', 'missing_required_field:task_name'],
      },
      bundle: {
        requiredFields: ['title'],
        filter: notArchived,
        excludeReasons: ['archived', 'missing_required_field:title'],
      },
      project: {
        requiredFields: ['project_name'],
        filter: notArchived,
        excludeReasons: ['archived', 'missing_required_field:project_name'],
      },
    },
  },
  projects: {
    entities: {
      project: {
        requiredFields: ['project_name'],
        filter: notArchived,
        excludeReasons: ['archived', 'missing_required_field:project_name'],
      },
      bundle: {
        // TICKET-320: a lead's bundle is deal-scoped (crm_deal_id) with NO
        // project yet — project_id is only set when the deal is won. Requiring
        // project_id wrongly excluded every deal/lead bundle from render
        // (render_exclusion telemetry, CEO 2026-07-20: voice-approved bundles
        // never reached the bundle board). Render when scoped to EITHER a
        // project OR a deal; a truly unscoped bundle still filter_rejects.
        requiredFields: ['title'],
        filter: (e) => !e.archived_at && (e.project_id != null || e.crm_deal_id != null),
        excludeReasons: ['archived', 'missing_required_field:title', 'filter_rejected'],
      },
    },
  },
  focus: {
    entities: {
      task: {
        requiredFields: ['task_name'],
        filter: notArchived,
        excludeReasons: ['archived', 'missing_required_field:task_name'],
      },
    },
  },
  pipeline: {
    entities: {
      deal: {
        requiredFields: ['opportunity'],
        filter: notArchived,
        excludeReasons: ['archived', 'missing_required_field:opportunity'],
      },
    },
  },
  operations: {
    entities: {
      task: {
        requiredFields: ['task_name'],
        filter: notArchived,
        excludeReasons: ['archived', 'missing_required_field:task_name'],
      },
    },
  },
  myday: {
    entities: {
      task: {
        requiredFields: ['task_name'],
        filter: notArchived,
        excludeReasons: ['archived', 'missing_required_field:task_name'],
      },
    },
  },
  command: {
    entities: {
      task: {
        requiredFields: ['task_name'],
        filter: notArchived,
        excludeReasons: ['archived', 'missing_required_field:task_name'],
      },
    },
  },
  team: {
    entities: {
      task: {
        requiredFields: ['task_name'],
        filter: notArchived,
        excludeReasons: ['archived', 'missing_required_field:task_name'],
      },
      bundle: {
        requiredFields: ['title'],
        filter: notArchived,
        excludeReasons: ['archived', 'missing_required_field:title'],
      },
    },
  },
  directory: {
    entities: {
      person: {
        requiredFields: ['display_name'],
        filter: alwaysAllow,
        excludeReasons: ['missing_required_field:display_name'],
      },
      vessel_contact: {
        // Raw vessel_contacts rows: first_name/last_name are separate base columns;
        // contact_name is a derived (view-only) field. Gate on actual base columns.
        // vessel_id is NOT required (CEO 2026-07-22): manual directory entry
        // creates boat-less contacts (broker, surveyor, supplier rep) and gating
        // on the anchor hid them from the very list they were added to.
        // TICKET-906: first_name is not required either. A company-only row
        // (company_name 'BWA', no person yet) is a real contact, and gating on
        // first_name hid it; only a row with nothing to show is excluded.
        requiredFields: [],
        filter: (c) => ['first_name', 'last_name', 'company_name', 'organization', 'email']
          .some(f => (c[f] || '').trim() !== ''),
        excludeReasons: ['filter_rejected'],
      },
    },
  },
  procurement: {
    entities: {
      purchase_order: {
        requiredFields: ['po_number', 'status'],
        filter: alwaysAllow,
        excludeReasons: ['missing_required_field:po_number', 'missing_required_field:status'],
      },
      supplier: {
        requiredFields: ['name'],
        filter: alwaysAllow,
        excludeReasons: ['missing_required_field:name'],
      },
    },
  },
  worklogs: {
    entities: {
      timelog: {
        requiredFields: ['date', 'duration_hours'],
        filter: alwaysAllow,
        excludeReasons: ['missing_required_field:date', 'missing_required_field:duration_hours'],
      },
    },
  },
};

const PLURAL_TO_SINGULAR = {
  tasks: 'task',
  bundles: 'bundle',
  projects: 'project',
  deals: 'deal',
  people: 'person',
  vesselContacts: 'vessel_contact',
  vessel_contacts: 'vessel_contact',
  purchaseOrders: 'purchase_order',
  purchase_orders: 'purchase_order',
  suppliers: 'supplier',
  timeLogs: 'timelog',
  time_logs: 'timelog',
  timelogs: 'timelog',
};

/**
 * applyContract(view, entityTypeOrPlural, candidates)
 *   → { visible: Entity[], excluded: Entity[] (each tagged _reason) }
 * Side-effect: logs one render_exclusion per excluded entity.
 */
export function applyContract(view, entityType, candidates = []) {
  const contract = VIEW_CONTRACTS[view];
  if (!contract) throw new Error(`Unknown view: ${view}`);

  const singular = PLURAL_TO_SINGULAR[entityType] || entityType;
  const spec = contract.entities[singular];
  if (!spec) throw new Error(`${entityType} not declared in ${view} contract`);

  const visible = [];
  const excluded = [];

  for (const e of candidates) {
    let missingField = null;
    for (const f of spec.requiredFields) {
      if (e[f] == null || e[f] === '') { missingField = f; break; }
    }
    if (missingField) {
      const reason = `missing_required_field:${missingField}`;
      excluded.push({ ...e, _reason: reason });
      logExclusion(view, singular, e.id, reason, { missing_field: missingField });
      continue;
    }

    if (!spec.filter(e)) {
      const reason = e.archived_at ? 'archived' : 'filter_rejected';
      excluded.push({ ...e, _reason: reason });
      logExclusion(view, singular, e.id, reason, {
        archived_at: e.archived_at || null,
      });
      continue;
    }

    visible.push(e);
  }

  return { visible, excluded };
}

export function logExclusion(view, entityType, entityId, reason, extra = {}) {
  logEvent('render_exclusion', `${view}:${entityType}:${entityId}:${reason}`, {
    phase_tag: PHASE_TAG,
    ...getTraceContext(),
    view,
    entity_type: entityType,
    entity_id: entityId,
    reason,
    ...extra,
  });
}
